import { useEffect, useMemo, useState } from "react";
import { Landmark, Save, CalendarClock, Clock, CheckCircle2, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage, formatRupiah, formatTanggalID } from "@/lib/api";
import { tipeBadgeClass } from "@/pages/admin/AdminKomponenGaji";

const PTKP = ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3"];

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function formatRibuan(v) {
  const digits = String(v ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// Bangun daftar komponen untuk payload/perbandingan dari state `selected`.
function buildKomponenList(selected) {
  return Object.entries(selected)
    .filter(([, v]) => v.checked)
    .map(([id, v]) => ({
      komponen_id: id,
      nilai_override: v.override === "" ? null : parseFloat(v.override),
      aktif: v.aktif !== false,
    }));
}

// Normalisasi → { [id]: { override: number|null, aktif: bool } } untuk diff.
function normalizeKomp(komponenArr) {
  const m = {};
  (komponenArr || []).forEach((c) => {
    m[c.komponen_id] = {
      override: c.nilai_override == null ? null : Number(c.nilai_override),
      aktif: c.aktif !== false,
    };
  });
  return m;
}

export function PayrollProfilTab({ userId, npp }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profil, setProfil] = useState(null);
  const [komponenMaster, setKomponenMaster] = useState([]);
  const [selected, setSelected] = useState({}); // { [id]: { checked, override } }
  const [form, setForm] = useState({ no_rekening: "", nama_bank: "", npwp: "", ptkp_status: "TK/0" });
  const [variabel, setVariabel] = useState(null);
  // Snapshot komponen saat load → dipakai mendeteksi perubahan upah.
  const [initialKomp, setInitialKomp] = useState({});
  // Dialog konfirmasi Tanggal Berlaku (muncul saat ada perubahan komponen upah).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [changeReason, setChangeReason] = useState("");
  const [changedLabels, setChangedLabels] = useState([]);

  // Identifikasi komponen sistem "Gaji Pokok"
  const gajiPokokKomp = useMemo(
    () => komponenMaster.find((k) => k.system_key === "gaji_pokok"),
    [komponenMaster]
  );
  const gajiPokokId = gajiPokokKomp?.id;
  const gajiPokok = useMemo(() => {
    if (!gajiPokokId) return 0;
    const st = selected[gajiPokokId];
    if (!st) return 0;
    return parseInt(st.override || "0", 10) || 0;
  }, [selected, gajiPokokId]);

  async function load() {
    setLoading(true);
    try {
      const [p, k, v] = await Promise.all([
        api.get(`/payroll/profil/${userId}`),
        api.get("/payroll/komponen", { params: { include_inactive: false } }),
        api.get("/payroll/variabel", { params: { user_id: userId } }),
      ]);
      setProfil(p.data);
      setKomponenMaster(k.data);
      setVariabel({ periode: v.data.periode, row: v.data.rows?.[0] || null });
      setForm({
        no_rekening: p.data.profil.no_rekening || "",
        nama_bank: p.data.profil.nama_bank || "",
        npwp: p.data.profil.npwp || "",
        ptkp_status: p.data.profil.ptkp_status || "TK/0",
      });
      const sel = {};
      (p.data.profil.komponen || []).forEach((c) => {
        sel[c.komponen_id] = {
          checked: true,
          override: c.nilai_override != null ? String(c.nilai_override) : "",
          aktif: c.aktif !== false,
        };
      });
      // Fallback: bila komponen 'Gaji Pokok' belum ada di profil (edge case
      // pegawai baru), gunakan nilai gaji_pokok dari profil sebagai seed.
      const gpMaster = (k.data || []).find((x) => x.system_key === "gaji_pokok");
      if (gpMaster && !sel[gpMaster.id]) {
        sel[gpMaster.id] = {
          checked: true,
          override: String(p.data.profil.gaji_pokok || 0),
          aktif: true,
        };
      }
      setSelected(sel);
      setInitialKomp(normalizeKomp(buildKomponenList(sel)));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  // Daftar label komponen yang berubah vs snapshot awal (untuk ditampilkan di dialog).
  function computeChangedLabels(currentKomp) {
    const cur = normalizeKomp(currentKomp);
    const labels = [];
    const ids = new Set([...Object.keys(initialKomp), ...Object.keys(cur)]);
    const nameOf = (id) => {
      const km = komponenMaster.find((x) => x.id === id);
      if (km?.system_key === "gaji_pokok") return "Gaji Pokok";
      return km?.nama || "Komponen";
    };
    ids.forEach((id) => {
      const b = initialKomp[id];
      const a = cur[id];
      if (!b && a) { labels.push(`${nameOf(id)} (baru)`); return; }
      if (b && !a) { labels.push(`${nameOf(id)} (dihapus)`); return; }
      if (b && a) {
        const ovChanged = (b.override ?? null) !== (a.override ?? null);
        const aktifChanged = b.aktif !== a.aktif;
        if (ovChanged || aktifChanged) labels.push(nameOf(id));
      }
    });
    return labels;
  }

  // Klik "Simpan": bila ada perubahan komponen upah → minta Tanggal Berlaku via dialog.
  function handleSaveClick() {
    const komponen = buildKomponenList(selected);
    const labels = computeChangedLabels(komponen);
    if (labels.length === 0) {
      doSave(null, null, komponen);
      return;
    }
    setChangedLabels(labels);
    setEffectiveDate(todayIso());
    setChangeReason("");
    setConfirmOpen(true);
  }

  async function doSave(tgl, reason, komponenArg) {
    setSaving(true);
    try {
      const komponen = komponenArg || buildKomponenList(selected);
      // Gaji Pokok diambil dari row komponen sistem (jadi tidak duplikat)
      const gpVal = gajiPokokId
        ? parseInt(selected[gajiPokokId]?.override || "0", 10) || 0
        : 0;
      const payload = {
        no_rekening: form.no_rekening.trim() || null,
        nama_bank: form.nama_bank.trim() || null,
        npwp: form.npwp.trim() || null,
        gaji_pokok: gpVal,
        ptkp_status: form.ptkp_status || "TK/0",
        komponen,
      };
      // Tanggal Berlaku (efektif) yang diinput admin → disimpan apa adanya ke
      // kolom tanggal_berlaku di employee_change_history (bukan tanggal pencatatan).
      if (tgl) payload.tanggal_berlaku = tgl;
      if (reason && reason.trim()) payload.change_reason = reason.trim();
      await api.put(`/payroll/profil/${userId}`, payload);
      toast.success("Profil payroll pegawai disimpan");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function confirmSave() {
    const tgl = (effectiveDate || "").trim();
    if (!tgl) {
      toast.error("Tanggal Berlaku wajib diisi");
      return;
    }
    await doSave(tgl, changeReason);
  }

  if (loading) {
    return <div className="py-10 text-center text-sm text-slate-500" data-testid="payroll-tab-loading">Memuat data payroll…</div>;
  }

  const row = variabel?.row;
  const hariHadir = row?.hari_hadir ?? 0;
  const jamLembur = row?.total_jam_lembur ?? 0;
  const nilaiEngine = {};
  (row?.komponen || []).forEach((c) => {
    if (c.komponen_id) nilaiEngine[c.komponen_id] = c.nilai;
  });

  function estimasi(k, overrideStr) {
    if (k.sifat === "Tidak Tetap") return nilaiEngine[k.id] || 0;
    if (k.sifat === "Tidak Tetap - Nominal") {
      const st = selected[k.id];
      if (st && st.aktif === false) return 0;
      const dasar = overrideStr === "" || overrideStr == null ? Number(k.nilai_default || 0) : parseFloat(overrideStr) || 0;
      return Math.round(dasar);
    }
    if (k.sifat === "Tahunan - Upah Tetap") {
      // Estimasi UI: Gaji Pokok + Σ komponen sifat Tetap yang tercentang.
      let basis = gajiPokok;
      Object.entries(selected).forEach(([id, v]) => {
        const km = komponenMaster.find((x) => x.id === id);
        if (!km || km.sifat !== "Tetap" || !v.checked) return;
        const dasar = v.override === "" || v.override == null ? Number(km.nilai_default || 0) : parseFloat(v.override) || 0;
        basis += Math.round(dasar);
      });
      return basis;
    }
    const dasar = overrideStr === "" || overrideStr == null ? Number(k.nilai_default || 0) : parseFloat(overrideStr) || 0;
    if (k.sifat === "Tetap") return Math.round(dasar);
    if (k.sifat === "Persentase") return Math.round((gajiPokok * dasar) / 100);
    if (k.sifat === "Berbasis Lembur") return Math.round(dasar * jamLembur);
    return Math.round(dasar * hariHadir);
  }

  return (
    <div className="space-y-5" data-testid="payroll-profil-tab">
      {/* Data bank & pajak */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-teal-800">
          <Landmark size={15} />
          <h3 className="text-sm font-extrabold">Data Bank &amp; Pajak</h3>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">NPP</Label>
            <Input value={npp || profil?.user?.npp || "—"} readOnly disabled className="mt-1.5 bg-slate-50 font-mono-clock" data-testid="payroll-npp" />
            <p className="text-[11px] text-slate-400 mt-1">Mengikuti data kepegawaian (Master Pegawai).</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">NPWP (opsional)</Label>
            <Input
              className="mt-1.5 font-mono-clock"
              placeholder="00.000.000.0-000.000"
              value={form.npwp}
              onChange={(e) => setForm({ ...form, npwp: e.target.value })}
              data-testid="payroll-npwp"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Status PTKP (PPh 21)</Label>
            <Select value={form.ptkp_status} onValueChange={(v) => setForm({ ...form, ptkp_status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="payroll-ptkp"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PTKP.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">TK = tidak kawin, K = kawin; angka = jumlah tanggungan.</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama Bank</Label>
            <Input
              className="mt-1.5"
              placeholder="Contoh: BNI"
              value={form.nama_bank}
              onChange={(e) => setForm({ ...form, nama_bank: e.target.value })}
              data-testid="payroll-nama-bank"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">No. Rekening</Label>
            <Input
              className="mt-1.5 font-mono-clock"
              inputMode="numeric"
              value={form.no_rekening}
              onChange={(e) => setForm({ ...form, no_rekening: e.target.value })}
              data-testid="payroll-no-rekening"
            />
          </div>
        </div>
        <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 flex items-start gap-2" data-testid="payroll-gp-notice">
          <Info size={14} className="text-slate-500 mt-0.5" />
          <div className="text-[11px] text-slate-600">
            <b>Gaji Pokok</b> kini dikelola bersama komponen lain di bagian{" "}
            <b>Komponen Gaji Aktif</b> di bawah (baris pertama, terkunci sebagai komponen sistem).
            Nilainya menjadi dasar kalkulasi Persentase, Lembur Kepmenaker, PPh 21, dan Tahunan.
          </div>
        </div>
      </div>

      {/* Komponen aktif */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-extrabold text-teal-800">Komponen Gaji Aktif</h3>
          <span className="text-[11px] text-slate-500">
            {Object.values(selected).filter((v) => v.checked).length} dipilih
          </span>
        </div>
        {komponenMaster.length === 0 ? (
          <div className="mt-3 text-sm text-slate-500 italic" data-testid="payroll-komponen-empty">
            Belum ada komponen aktif di Master Komponen. Tambahkan lewat menu <b>Master Komponen</b>.
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {[...komponenMaster].sort((a, b) => {
              // Row 'Gaji Pokok' selalu di paling atas
              if (a.system_key === "gaji_pokok") return -1;
              if (b.system_key === "gaji_pokok") return 1;
              return (a.urutan ?? 0) - (b.urutan ?? 0);
            }).map((k) => {
              const st = selected[k.id] || { checked: false, override: "", aktif: true };
              const isPersen = k.sifat === "Persentase";
              const isSystem = k.system_key === "gaji_pokok";
              return (
                <div
                  key={k.id}
                  className={`rounded-lg border p-3 transition-colors ${
                    isSystem
                      ? "border-teal-600 bg-teal-50 shadow-sm"
                      : st.checked
                      ? "border-teal-200 bg-teal-50/40"
                      : "border-slate-200 bg-white"
                  }`}
                  data-testid={`payroll-komponen-${k.id}`}
                >
                  <div className="flex items-start gap-3">
                    {isSystem ? (
                      <div
                        className="mt-0.5 w-4 h-4 rounded bg-teal-700 flex items-center justify-center shrink-0"
                        title="Komponen sistem — selalu aktif"
                        data-testid={`payroll-komponen-lock-${k.id}`}
                      >
                        <Lock size={9} className="text-white" />
                      </div>
                    ) : (
                      <Checkbox
                        checked={st.checked}
                        onCheckedChange={(v) =>
                          setSelected({ ...selected, [k.id]: { ...st, checked: !!v, override: st.override } })
                        }
                        className="mt-0.5"
                        data-testid={`payroll-komponen-check-${k.id}`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">{k.nama}</span>
                        {isSystem && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal-700 text-white">
                            <Lock size={9} /> Sistem
                          </span>
                        )}
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${tipeBadgeClass(k.tipe)}`}>
                          {k.tipe}
                        </span>
                        {k.kena_pajak ? (
                          <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">Taxable</span>
                        ) : null}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {isSystem
                          ? "Basis kalkulasi Persentase, Lembur, PPh 21, dan Tahunan."
                          : `${k.sifat} — ${isPersen ? `${k.nilai_default}%` : k.sifat === "Tidak Tetap" ? "nilai diisi per bulan (menu Komponen Tidak Tetap)" : `default ${formatRupiah(k.nilai_default)}`}`}
                      </div>
                      {(st.checked || isSystem) ? (
                        <div className="mt-2 flex items-end gap-3 flex-wrap">
                          {k.sifat === "Tidak Tetap" || k.sifat === "Tahunan - Upah Tetap" ? null : (
                          <div className="w-44">
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              {isSystem ? "Nominal Gaji Pokok" : "Override (opsional)"}
                            </Label>
                            <div className="relative mt-1">
                              {!isPersen ? (
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">Rp</span>
                              ) : null}
                              <Input
                                inputMode="numeric"
                                className={`h-9 text-sm ${isPersen ? "" : "pl-8"} font-mono-clock`}
                                placeholder={isPersen ? `${k.nilai_default}` : "pakai default"}
                                value={isPersen ? st.override : formatRibuan(st.override)}
                                onChange={(e) =>
                                  setSelected({
                                    ...selected,
                                    [k.id]: { ...st, checked: true, override: e.target.value.replace(/\D/g, "").slice(0, 12) },
                                  })
                                }
                                data-testid={`payroll-komponen-override-${k.id}`}
                              />
                              {isPersen ? (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">%</span>
                              ) : null}
                            </div>
                          </div>
                          )}
                          {k.sifat === "Tidak Tetap - Nominal" ? (
                            <div className="flex items-center gap-2 pb-2" data-testid={`aktif-toggle-wrap-${k.id}`}>
                              <Switch
                                checked={st.aktif !== false}
                                onCheckedChange={(v) =>
                                  setSelected({
                                    ...selected,
                                    [k.id]: { ...st, aktif: !!v },
                                  })
                                }
                                data-testid={`aktif-toggle-${k.id}`}
                              />
                              <span className={`text-xs font-semibold ${st.aktif !== false ? "text-emerald-700" : "text-slate-400"}`}>
                                {st.aktif !== false ? "Aktif" : "Non-aktif"}
                              </span>
                            </div>
                          ) : null}
                          <div className="text-xs text-slate-600 pb-2">
                            Estimasi periode ini:{" "}
                            <b className={k.tipe === "Potongan" ? "text-rose-700" : k.tipe === "Beban Perusahaan" ? "text-indigo-700" : "text-emerald-700"}>
                              {formatRupiah(estimasi(k, st.override))}
                            </b>
                            {k.sifat === "Tahunan - Upah Tetap" ? (
                              <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5">
                                Hanya pada bulan Anniversary
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Variabel absensi & lembur */}
      <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4" data-testid="payroll-variabel-panel">
        <div className="flex items-center gap-2 text-teal-800">
          <CalendarClock size={15} />
          <h3 className="text-sm font-extrabold">
            Variabel Periode {variabel?.periode?.label || "—"}
          </h3>
        </div>
        <p className="text-[11px] text-teal-900/70 mt-1">
          Cutoff {formatTanggalID(variabel?.periode?.start)} – {formatTanggalID(variabel?.periode?.end)} · Gajian{" "}
          {formatTanggalID(variabel?.periode?.tanggal_gajian)}
        </p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <MiniStat label="Hari Kerja" value={row?.hari_kerja_periode ?? 0} testId="var-hari-kerja" />
          <MiniStat label="Hari Hadir" value={row?.hari_hadir ?? 0} testId="var-hari-hadir" />
          <MiniStat label="Terlambat" value={row?.hari_terlambat ?? 0} testId="var-terlambat" />
          <MiniStat label="Alpha" value={row?.hari_alpha ?? 0} testId="var-alpha" />
          <MiniStat label="Jam Lembur" value={row?.total_jam_lembur ?? 0} icon={Clock} testId="var-jam-lembur" />
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
          <div className="rounded-md bg-white border border-teal-100 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Total Pendapatan</div>
            <div className="font-extrabold text-emerald-700 mt-1" data-testid="var-total-pendapatan">
              {formatRupiah(row?.bruto)}
            </div>
          </div>
          <div className="rounded-md bg-white border border-teal-100 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Total Potongan</div>
            <div className="font-extrabold text-rose-700 mt-1" data-testid="var-total-potongan">
              {formatRupiah(row?.total_potongan)}
            </div>
          </div>
          <div className="rounded-md bg-white border border-teal-100 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">PPh 21 / bulan</div>
            <div className="font-extrabold text-amber-800 mt-1" data-testid="var-pph21">
              {formatRupiah(row?.pph21_bulan)}
            </div>
          </div>
          <div className="rounded-md bg-white border border-teal-100 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Estimasi Diterima</div>
            <div className="font-extrabold text-teal-800 mt-1" data-testid="var-take-home">
              {formatRupiah(row?.netto)}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-teal-900/80">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            Angka <b>estimasi</b> memakai Payroll Engine yang sama dengan Run Payroll (absensi, lembur, PPh 21).
            Metode lembur: <b>{row?.lembur_metode || "—"}</b> · Potongan absensi: <b>{formatRupiah(row?.potongan_absensi)}</b> ·
            Dasar pajak: <b>{formatRupiah(row?.dasar_pajak)}</b>.
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] text-slate-500">
          {profil?.profil?.tersimpan ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 size={12} /> Terakhir disimpan oleh {profil.profil.updated_by_nama || "—"}
            </span>
          ) : (
            "Profil payroll belum pernah disimpan untuk pegawai ini."
          )}
        </div>
        <Button onClick={handleSaveClick} disabled={saving} className="bg-teal-700 hover:bg-teal-800 gap-1.5" data-testid="payroll-save-btn">
          <Save size={14} /> {saving ? "Menyimpan…" : "Simpan Profil Payroll"}
        </Button>
      </div>

      {/* Dialog konfirmasi Tanggal Berlaku untuk perubahan komponen upah */}
      <Dialog open={confirmOpen} onOpenChange={(v) => { if (!saving) setConfirmOpen(v); }}>
        <DialogContent data-testid="payroll-effective-dialog">
          <DialogHeader>
            <DialogTitle>Konfirmasi Perubahan Komponen Upah</DialogTitle>
            <DialogDescription>
              Perubahan komponen upah berikut akan dicatat ke Riwayat Perubahan.
              Tentukan <b>Tanggal Berlaku</b> (tanggal efektif) perubahan ini.
            </DialogDescription>
          </DialogHeader>
          {changedLabels.length ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              <div className="font-bold uppercase tracking-wider text-slate-500 mb-1">Komponen yang berubah</div>
              <ul className="list-disc pl-4 space-y-0.5" data-testid="payroll-effective-changed-list">
                {changedLabels.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Tanggal Berlaku <span className="text-rose-600">*</span>
            </Label>
            <Input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="mt-1.5"
              data-testid="payroll-effective-date"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Default: hari ini. Boleh dimundurkan (backdate) ke tanggal lampau atau dimajukan ke tanggal mendatang.
              Nilai inilah yang disimpan sebagai tanggal efektif di Riwayat Perubahan, terpisah dari tanggal pencatatan sistem.
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Perubahan (Opsional)
            </Label>
            <Textarea
              placeholder="Contoh: Penyesuaian gaji sesuai SK No. 123/2026"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              rows={3}
              maxLength={400}
              className="mt-1.5"
              data-testid="payroll-effective-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving} data-testid="payroll-effective-cancel">
              Batal
            </Button>
            <Button
              onClick={confirmSave}
              disabled={saving || !effectiveDate}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="payroll-effective-confirm"
            >
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, testId }) {
  return (
    <div className="rounded-md bg-white border border-teal-100 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold flex items-center gap-1">
        {Icon ? <Icon size={11} /> : null} {label}
      </div>
      <div className="text-xl font-extrabold text-slate-900 mt-1 leading-none font-mono-clock">{value}</div>
    </div>
  );
}

export default PayrollProfilTab;
